import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Mail, Info } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/brand/Logo";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
    InputOTPSeparator,
} from "@/components/ui/input-otp";

/** Convert FastAPI error detail (string | array | object) into a readable string. */
function readError(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
        return d.map((x) => (typeof x?.msg === "string" ? x.msg : JSON.stringify(x))).join(" · ");
    }
    if (d?.msg) return d.msg;
    return err?.message || "Bir hata oluştu";
}

export default function Login() {
    const { t } = useI18n();
    const { session, requestOtp, verifyOtp } = useAuth();
    const nav = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [otpError, setOtpError] = useState("");
    const [devHint, setDevHint] = useState("");
    const [seconds, setSeconds] = useState(0);
    const otpInputRef = useRef(null);

    // Already logged in? bounce to app.
    useEffect(() => {
        if (session && session.user) {
            const from = location.state?.from || "/app";
            nav(from, { replace: true });
        }
    }, [session, nav, location.state]);

    useEffect(() => {
        if (seconds <= 0) return;
        const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [seconds]);

    useEffect(() => {
        if (step === "otp") {
            setOtp("");
            setOtpError("");
            setTimeout(() => otpInputRef.current?.focus(), 50);
        }
    }, [step]);

    const handleContinue = async (e) => {
        e?.preventDefault();
        if (!email || busy) return;
        setBusy(true);
        setEmailError("");
        setDevHint("");
        try {
            const res = await requestOtp(email.trim().toLowerCase());
            setStep("otp");
            setSeconds(30);
            if (res?.dev_code) {
                setDevHint(`(Dev) Kod: ${res.dev_code}`);
            }
        } catch (err) {
            setEmailError(readError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async () => {
        if (seconds > 0 || busy) return;
        setBusy(true);
        try {
            const res = await requestOtp(email.trim().toLowerCase());
            setSeconds(30);
            if (res?.dev_code) setDevHint(`(Dev) Kod: ${res.dev_code}`);
        } catch (err) {
            setOtpError(readError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleVerify = async (code) => {
        if (code.length !== 6 || busy) return;
        setBusy(true);
        setOtpError("");
        try {
            await verifyOtp(email.trim().toLowerCase(), code);
            const from = location.state?.from || "/app";
            nav(from, { replace: true });
        } catch (err) {
            setOtpError(readError(err));
            setOtp("");
        } finally {
            setBusy(false);
        }
    };

    // Auto-verify when 6 digits typed
    useEffect(() => {
        if (otp.length === 6) handleVerify(otp);
        else if (otpError) setOtpError("");
         
    }, [otp]);

    return (
        <div className="min-h-screen relative bg-background text-foreground overflow-hidden">
            <div className="pointer-events-none absolute inset-0 dot-bg opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
            <div className="pointer-events-none absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full blur-3xl opacity-30 bg-brand/30" />

            <header className="relative z-10 h-16 px-6 md:px-8 flex items-center justify-between">
                <Logo />
            </header>

            <main className="relative z-10 grid place-items-center px-6" style={{ minHeight: "calc(100vh - 64px - 48px)" }}>
                <div className="w-full max-w-[420px]">
                    <div className="mb-8 flex items-center justify-center">
                        <div className="h-12 w-12 rounded-xl border border-border bg-surface flex items-center justify-center">
                            {step === "email" ? (
                                <Mail className="h-5 w-5" strokeWidth={1.5} />
                            ) : (
                                <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
                            )}
                        </div>
                    </div>

                    {step === "email" ? (
                        <form onSubmit={handleContinue} className="space-y-6 animate-fade-in">
                            <div className="text-center space-y-2">
                                <h1 className="font-display text-3xl font-bold tracking-tight">{t("login.welcome")}</h1>
                                <p className="text-sm text-muted-foreground">{t("login.subtitle")}</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground" htmlFor="email">
                                    {t("login.email")}
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t("login.placeholder")}
                                    className="h-11 text-sm bg-surface"
                                    data-testid="login-email-input"
                                    disabled={busy}
                                />
                                {emailError && (
                                    <p className="text-xs text-destructive pt-1" data-testid="login-email-error">{emailError}</p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                disabled={busy || !email}
                                className="w-full h-11 bg-brand hover:bg-brand/90 text-brand-fg font-medium disabled:opacity-60"
                                data-testid="login-continue-button"
                            >
                                {busy ? "Kod gönderiliyor…" : t("common.continue")}
                                {!busy && <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2} />}
                            </Button>

                            <p className="text-xs text-center text-muted-foreground leading-relaxed">
                                {t("login.invitedOnly")}
                            </p>
                        </form>
                    ) : (
                        <div className="space-y-6 animate-fade-in">
                            <div className="text-center space-y-2">
                                <h1 className="font-display text-3xl font-bold tracking-tight">{t("login.otp.title")}</h1>
                                <p className="text-sm text-muted-foreground px-2">
                                    {t("login.otp.subtitle", { email })}
                                </p>
                            </div>

                            {devHint && (
                                <div
                                    className="rounded-lg border-2 border-info/40 bg-info-soft px-4 py-3 flex items-center justify-between gap-3 w-full shadow-sm animate-fade-in"
                                    data-testid="login-dev-hint"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="h-8 w-8 rounded-md bg-info/15 text-info grid place-items-center shrink-0">
                                            <Info className="h-4 w-4" strokeWidth={2} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] font-semibold uppercase tracking-wider text-info/80">
                                                Geliştirici Modu
                                            </div>
                                            <div className="font-mono text-lg font-bold text-info tabular-nums leading-tight" data-testid="login-dev-code">
                                                {devHint.replace(/^\(Dev\) Kod:\s*/, "")}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const code = devHint.replace(/^\(Dev\) Kod:\s*/, "").trim();
                                            if (code.length === 6) setOtp(code);
                                        }}
                                        className="shrink-0 rounded-md bg-info text-white text-xs font-medium px-3 py-1.5 hover:opacity-90 transition-opacity"
                                        data-testid="login-dev-autofill-btn"
                                    >
                                        Kodu doldur
                                    </button>
                                </div>
                            )}

                            <div className="flex justify-center">
                                <InputOTP
                                    ref={otpInputRef}
                                    maxLength={6}
                                    value={otp}
                                    onChange={(v) => setOtp(v)}
                                    data-testid="login-otp-input"
                                    disabled={busy}
                                >
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                        <InputOTPSlot index={1} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                        <InputOTPSlot index={2} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                    </InputOTPGroup>
                                    <InputOTPSeparator />
                                    <InputOTPGroup>
                                        <InputOTPSlot index={3} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                        <InputOTPSlot index={4} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                        <InputOTPSlot index={5} className={`w-12 h-14 text-lg font-mono ${otpError ? "border-destructive" : ""}`} />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>

                            {otpError && (
                                <p className="text-xs text-center text-destructive -mt-2" data-testid="login-otp-error">
                                    {otpError}
                                </p>
                            )}

                            <Button
                                onClick={() => handleVerify(otp)}
                                disabled={otp.length !== 6 || busy}
                                className="w-full h-11 bg-brand hover:bg-brand/90 text-brand-fg font-medium disabled:opacity-60"
                                data-testid="login-verify-button"
                            >
                                {busy ? "Doğrulanıyor…" : t("common.verify")}
                                {!busy && <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2} />}
                            </Button>

                            <div className="flex items-center justify-between text-xs">
                                <button
                                    onClick={() => setStep("email")}
                                    className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
                                    data-testid="login-back-btn"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5" /> {t("common.back")}
                                </button>
                                <button
                                    disabled={seconds > 0 || busy}
                                    onClick={handleResend}
                                    className="text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:hover:text-muted-foreground transition-colors"
                                    data-testid="login-resend-btn"
                                >
                                    {seconds > 0 ? t("login.otp.resentIn", { s: seconds }) : t("common.resend")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="relative z-10 h-12 px-6 md:px-8 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>© Sayz {new Date().getFullYear()}</span>
                <span className="font-mono">v1.0.0 · build a1c4</span>
            </footer>
        </div>
    );
}
